import styled from "@emotion/styled";
import assertNever from "assert-never";
import { motion } from "framer-motion";
import React from "react";
import { API } from "../../API";
import { HRQuestionGroup } from "../../messages/HR";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { AppButton } from "../Generic/AppButton";
import { AppCard, AppCardContent } from "../Generic/AppCard";
import { AppTextButton } from "../Generic/AppTextButton";
import Flex from "../Generic/Flex";
import { If } from "../Generic/If";
import { InfoCard } from "../Generic/InfoCard";
import {
  StageStatus,
  getStageRejectedQuestionsDataMap,
  getStageStatus,
  isAnswerableQuestion,
  isQuestionAnswered,
  isQuestionGroupContainSignatureQuestion,
  isRelevantQuestion,
} from "../Onboarding/utils/onboardingUtils";
import Scaffold from "../Scaffold/Scaffold";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import OnboardingQuestionGroup from "./OnboardingQuestionGroup";
import OnboardingSectionAppCardHeader from "./OnboardingSectionAppCardHeader";
import ProgressBar from "./ProgressBar";
import useOnboardingForm from "./hooks/useOnboardingForm";
import { HRQuestionTemplateId } from "../../messages";
import { getDeviceType } from "../../utils/DeviceUtils";
import { TwotoneShareIcon } from "../../utils/icones";
import { AppIcon } from "../Generic/AppIcon";
import useShare from "../../hooks/useShare";

interface Props {
  title: string;
  titleSecondary: string;
  questionGroups: HRQuestionGroup[];
  currentStageNumber: number;
  percentage: number;
  totalStages: number;
  currentStage: HRQuestionGroup;
  isStageLoading: boolean;
  language: string;
  isSectionCompleted: boolean;
  isSignaureDisabled: boolean;
  emailVerificationTemplateId: HRQuestionTemplateId | null;
  isReferenceSection: boolean;
  shareUrl: string | null;
  isCompliant: boolean | null;
  answerMutationFn: API["setHRApplicationAnswer"];
  onRequestPreviousStage: () => void;
  onCompleteGroup: (group: HRQuestionGroup) => void;
  onClickGoBack: () => void;
  onInvalidSubmit: (params: { title: string; message: string }) => void;
}

const OnboardingSectionScreen = (props: Props) => {
  const stageStatus = getStageStatus(
    props.currentStage,
    props.isSectionCompleted,
    props.isCompliant
  );
  const isReadOnly =
    (stageStatus === "approved" ||
      (stageStatus === "completed" &&
        props.currentStage.questions.filter(isRelevantQuestion).every(isQuestionAnswered))) &&
    !props.isReferenceSection;

  const form = useOnboardingForm({
    emailVerificationTemplateId: props.emailVerificationTemplateId,
    questionGroups: props.questionGroups,
    currentStage: props.currentStage,
    isStageLoading: props.isStageLoading,
    answerMutationFn: props.answerMutationFn,
    onCompleted: props.onCompleteGroup,
    onInvalidSubmit: props.onInvalidSubmit,
    isStageReadOnly: isReadOnly,
  });

  const rejectedQuestionData = getStageRejectedQuestionsDataMap(props.currentStage);
  const hasAnswerableQuestions =
    props.currentStage.questions.filter(isAnswerableQuestion).length > 0;

  const { share } = useShare();

  const handleClickShare = () => {
    if (props.shareUrl !== null) {
      share({
        text: translate(STRINGS.HI_PLEASE_HELP_ME_FILL_MY_APPLICATION),
        url: props.shareUrl,
      });
    }
  };

  return (
    <Form
      data-fixed-layout={getDeviceType() === "ios-safari"}
      role="form"
      onSubmit={form.actions.handleSubmit}
    >
      <Scaffold
        title={<ScaffoldTitle label={props.title} />}
        onClickGoBack={props.onClickGoBack}
        footer={
          <ButtonWrapper>
            <AppButton type="submit" isLoading={form.state.isLoading}>
              {translate(STRINGS.NEXT)}
            </AppButton>
          </ButtonWrapper>
        }
        endActions={
          props.shareUrl !== null && (
            <ShareButton icon={<TwotoneShareIcon />} onClick={handleClickShare} />
          )
        }
      >
        <ContentPadding>
          <Flex direction="column" yGap={16}>
            <HeaderProgressBar
              currentStageNumber={props.currentStageNumber}
              percentage={props.percentage}
              totalStages={props.totalStages}
              onRequestPreviousStage={props.onRequestPreviousStage}
            />
            <HeaderCards
              stageStatus={stageStatus}
              isSignaureDisabled={props.isSignaureDisabled}
              hasSignatureQuestion={isQuestionGroupContainSignatureQuestion(props.currentStage)}
              isReadOnly={isReadOnly}
              rejectReasons={[...rejectedQuestionData.values()]}
              hasAnswerableQuestions={hasAnswerableQuestions}
            />
            <AppCard>
              <OnboardingSectionAppCardHeader title={props.titleSecondary} />
              <AppCardContent>
                <OnboardingQuestionGroup
                  questionGroup={props.currentStage}
                  title={props.titleSecondary}
                  language={props.language}
                  control={form.state.control}
                  isReadOnly={isReadOnly}
                  isSignaureDisabled={props.isSignaureDisabled}
                  emailVerificationTemplateId={props.emailVerificationTemplateId}
                  register={form.actions.register}
                  setValue={form.actions.setValue}
                  getValues={form.actions.getValues}
                  watch={form.actions.watch}
                  onError={props.onInvalidSubmit}
                />
              </AppCardContent>
            </AppCard>
          </Flex>
        </ContentPadding>
      </Scaffold>
    </Form>
  );
};

function HeaderProgressBar(props: {
  currentStageNumber: number;
  percentage: number;
  totalStages: number;
  onRequestPreviousStage: () => void;
}) {
  return (
    <AppCard color="primary" variant="soft">
      <AppCardContent>
        <Flex direction="column" yGap={16}>
          <ProgressBarWrapper>
            <If condition={props.currentStageNumber > 1}>
              <AppTextButton color={"primary"} onClick={props.onRequestPreviousStage} type="button">
                « {translate(STRINGS.PREVIOUS)}
              </AppTextButton>
            </If>
            <ProgressBar percentage={props.percentage} />
            <span>
              <b>{props.currentStageNumber}</b> {translate(STRINGS.STAGE_X_OF_TOTAL_STAGES)}{" "}
              {props.totalStages}
            </span>
          </ProgressBarWrapper>
        </Flex>
      </AppCardContent>
    </AppCard>
  );
}

function HeaderCards(props: {
  stageStatus: StageStatus;
  isReadOnly: boolean;
  isSignaureDisabled: boolean;
  hasSignatureQuestion: boolean;
  rejectReasons: string[];
  hasAnswerableQuestions: boolean;
}) {
  if (!props.hasAnswerableQuestions) {
    return null;
  }

  return (
    <Flex direction="column" yGap={16}>
      <SectionStatusCard
        stageStatus={props.stageStatus}
        rejectReasons={props.rejectReasons}
        isReadOnly={props.isReadOnly}
      />
      <SignatureInfoCard
        isSignaureDisabled={props.isSignaureDisabled}
        hasSignatureQuestion={props.hasSignatureQuestion}
        sectionStatus={props.stageStatus}
      />
    </Flex>
  );
}

function SignatureInfoCard(props: {
  isSignaureDisabled: boolean;
  hasSignatureQuestion: boolean;
  sectionStatus: StageStatus;
}) {
  if (
    !props.isSignaureDisabled ||
    !props.hasSignatureQuestion ||
    props.sectionStatus === "rejected"
  ) {
    return null;
  }

  return (
    <InfoCard
      variant="filled"
      color="primary"
      title={translate(STRINGS.YOU_CANT_SIGN_BEFORE_ALL_ANSWERED)}
    />
  );
}

const Text = styled.p`
  margin: 0;
`;

function SectionStatusCard(props: {
  stageStatus: StageStatus;
  rejectReasons: string[];
  isReadOnly: boolean;
}) {
  switch (props.stageStatus) {
    case "rejected":
      return (
        <InfoCard variant="soft" color="danger" title={translate(STRINGS.QUESTION_REJECTED)}>
          {translate(STRINGS.REASON)}: {props.rejectReasons.join(", ")}
          {props.isReadOnly && <Text>{translate(STRINGS.YOU_CANNOT_EDIT_YOUR_ANSWERS)}</Text>}
        </InfoCard>
      );

    case "completed":
      return (
        <InfoCard variant="filled" color="primary" title={translate(STRINGS.COMPLETED)}>
          {translate(STRINGS.STAGE_COMPLETED_MESSAGE)}
          {props.isReadOnly && <Text>{translate(STRINGS.YOU_CANNOT_EDIT_YOUR_ANSWERS)}</Text>}
        </InfoCard>
      );

    case "approved":
      return (
        <InfoCard variant="filled" color="teal" title={translate(STRINGS.APPROVED)}>
          {translate(STRINGS.STAGE_APPROVED_MESSAGE)}
          {props.isReadOnly && <Text>{translate(STRINGS.YOU_CANNOT_EDIT_YOUR_ANSWERS)}</Text>}
        </InfoCard>
      );

    case "pending":
      return (
        <InfoCard variant="filled" color="primary" title={translate(STRINGS.PENDING_APPROVAL)}>
          {translate(STRINGS.STAGE_AWAITING_APPROVAL)}
          {props.isReadOnly && <Text>{translate(STRINGS.YOU_CANNOT_EDIT_YOUR_ANSWERS)}</Text>}
        </InfoCard>
      );

    case "pendingEmail":
      return (
        <InfoCard variant="filled" color="primary" title={translate(STRINGS.PENDING_VERIFICATION)}>
          {translate(STRINGS.EMAIL_QUESTION_PENDING_VERIFICATION)}
          {props.isReadOnly && <Text>{translate(STRINGS.YOU_CANNOT_EDIT_YOUR_ANSWERS)}</Text>}
        </InfoCard>
      );

    case "not_answered":
      return null;

    default:
      return assertNever(props.stageStatus);
  }
}

const ContentPadding = styled.div`
  padding: 16px;
`;

const ButtonWrapper = styled.div`
  padding: 16px;
  display: flex;
  .AppButton {
    flex: 1;
  }
`;

const ProgressBarWrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;

  > * {
    font-size: 14px;
    line-height: 100%;
    margin: 0;
    padding: 0;
  }
`;

const Form = styled.form`
  height: 100%;
`;

const ShareButton = styled(AppIcon)`
  background: var(--gray-50);
  padding: 4px;
  border-radius: 4px;
  height: 28px;
  width: 28px;
`;

export default OnboardingSectionScreen;
